import React from 'react';
import Img from 'gatsby-image';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import SEO from '../components/SEO';
import localize from '../components/localize';
import Footer from '../components/Footer';
import { PageStyles } from '../styles/PageStyles';
import Gallery from '../components/Gallery';
import Vimeo from '../components/Vimeo';
import { breakpoints } from '../styles/Variables';
import musaBadge from '../assets/images/musa-badge-gold.png';
import GeneralPageSections from '../components/GeneralPageSections';
import JournalPagesPreview from '../components/JournalPagesPreview';

const GeneralPageStyles = styled.div`
  padding-bottom: 3rem;

  h1 {
    padding: 1em var(--col1);
    font-size: var(--fsTitle1);
    margin: 0;
  }

  blockquote {
    text-align: center;
    margin: 2rem 0;
  }

  .block:not(.noByline) {
    blockquote {
      em {
        display: block;
        padding-top: 1rem;
      }
    }
  }

  .sections {
    padding: 0 var(--col1);
  }

  .hero {
    max-height: 40vh;
  }

  // ===== TABLET =====
  @media (min-width: ${breakpoints.breakMd}) {
    .sections {
      position: relative;
      padding-top: 2rem;

      // Add extra side padding to journal page blocks (text heavy)
      .pageSection:not(.gallery) {
        padding-left: 3rem;
        padding-right: 3rem;
      }
    }

    &.contentPage {
      h1 {
        position: absolute;
        font-size: var(--fsTitle1);
        transform: rotate(90deg);
        transform-origin: 0 0;
        padding-left: 0;
        margin-top: 2rem;
        left: var(--col12);
      }

      .sections {
        .intro > * {
          /* padding-left: calc(var(--col2) + 1rem); */
          padding-right: var(--col2);
        }
      }
    }

    blockquote {
      text-align: center;
    }

    .block:not(.noByline) {
      blockquote {
        em {
          text-align: center;
        }
      }
    }
  }

  // ===== DESKTOP =====
  @media (min-width: ${breakpoints.breakLg}) {
    &.contentPage {
      padding-top: 3rem;

      h1 {
        transform: rotate(-90deg);
        transform-origin: left bottom;
        top: 50vh;
        width: 50vh;
        left: calc(var(--col1) + 0.5em);
        text-align: center;
      }

      .hero {
        margin-right: var(--col1);
        margin-left: var(--col1);
        margin-bottom: 2rem;
      }

      .hero.video {
        padding-bottom: 38% !important; // TODO: adjust with aspect ratio
      }

      .sections {
        // Content page intro
        .intro {
          & > * {
            padding-left: 0;
          }
        }
      }
    }

    .hero.gallery {
      width: auto;
    }
  }

  // Specific page templates
  &.termsConditions,
  &.faq {
    h1 {
      position: initial;
      transform: unset;
      width: 100%;
      padding: 2rem var(--col1) 0 var(--col1);
      margin-bottom: 2rem;

      @media (min-width: ${breakpoints.breakLg}) {
        padding: 0;
      }
    }
  }

  &.vision {
    .sections {
      background-image: url(${musaBadge});
      background-repeat: no-repeat;
      background-position: var(--col11) bottom;
      background-size: 6rem;
      padding-bottom: 6rem;
    }
  }

  // Journal pages
  &.journalPage {
    background-color: var(--peach);
    padding-bottom: 0;

    // Journal category
    h2 {
      font-size: var(--fsBody);
      text-align: center;
      margin-top: 2rem;
    }

    // Hero image
    img.hero {
      // TODO: full-bleed, 2:1 ratio
      height: 50vw;
    }

    .vimeo {
      margin-top: 2rem;
      margin-bottom: 4rem;
    }

    .pageSection {
      padding-bottom: 1rem;

      a:not(.button) {
        border-bottom: 1px solid var(--black);
        transition: border 0.5s;

        &:hover {
          border-bottom-color: transparent;
        }
      }
    }

    .pageSection.gallery {
      padding-bottom: 0;
      margin-top: 2rem;
      margin-bottom: 4rem;

      // Full bleed on smaller screens
      @media (max-width: ${breakpoints.breakMd}) {
        width: auto;
        margin-left: calc(-1 * var(--col1Internal));
        margin-right: calc(-1 * var(--col1Internal));
      }
    }

    .pageSection:not(.textAlignLeft):not(.intro) {
      p {
        text-align: justify;
      }

      .block.center p {
        text-align: center;
      }
    }

    .sections {
      // intro section styles
      .intro {
        .block:first-of-type {
          h1 {
            text-transform: none;
            font-size: var(--fsTitle1);
            font-family: 'Baskerville', serif;
            margin-bottom: var(--fsTitle1);
            margin-top: var(--fsTitle2);
            padding: 0;
          }

          p {
            font-size: 1.25em;
            max-width: 18em;
            margin-left: auto;
            margin-right: auto;
            font-size: var(--fsQuote);
          }
        }

        // Override justified
        .block.center p {
          text-align: center;
        }
      }
    }

    .slick-slide {
      height: 50vw;
    }

    .slick-initialized .slick-slide {
      max-height: 60vh;
    }

    .gallery .expanded .slick-initialized .slick-slide {
      max-height: unset;
    }
  }
`;

const GeneralPage = ({ data, pageContext }) => {
  const { page, allPages } = data;
  const thisPageId = page.id;
  const siteSettings = data.siteSettings.nodes[0];

  const isJournalPage = !!page.categories.length;
  const [journalCategory] = page.categories;
  const pageClassname = `${page.className} ${
    isJournalPage ? 'journalPage' : 'contentPage'
  }`;

  return (
    <>
      <SEO
        title={page.metaname}
        description={page.metaDescription}
        image={page.metaImage ? page.metaImage.asset.fluid.src : null}
      />
      <PageStyles className={pageClassname}>
        <GeneralPageStyles className={pageClassname}>
          {/* ==== Hero section ==== */}
          {page.heroVideoVimeoID && (
            <Vimeo
              className="hero video"
              vimeoID={page.heroVideoVimeoID}
              heightRatio={page.heroVideoRatioPercent || 50}
              autoplay
              controls
            />
          )}

          {page.heroImage && (
            <Img
              fluid={page.heroImage.asset.fluid}
              alt={page.name}
              className="hero"
            />
          )}

          {page.heroGallery && (
            <Gallery
              imageMaxWidth={pageContext.imageMaxWidth}
              images={page.heroGallery._rawImages}
              className="hero gallery"
            />
          )}

          {!isJournalPage && <h1>{page.name}</h1>}
          {isJournalPage && <h2>{journalCategory.name}</h2>}

          {/* ==== Page sections ==== */}
          {/* TODO: insert [go to top] link after first section */}
          {page._rawSections && (
            <GeneralPageSections page={page} pageContext={pageContext} />
          )}

          {/* ==== More journal pages ==== */}
          {isJournalPage && allPages && (
            <JournalPagesPreview pages={allPages} currentPageId={thisPageId} />
          )}
        </GeneralPageStyles>
      </PageStyles>
      {siteSettings && <Footer siteSettings={siteSettings} />}
    </>
  );
};

export default localize(GeneralPage);

export const query = graphql`
  query($pageId: String!, $imageMaxWidth: Int!) {
    siteSettings: allSanitySiteSettings {
      nodes {
        ...SiteSettingsFields
      }
    }
    allPages: allSanityGeneralPage(
      sort: { fields: _createdAt, order: DESC }
      filter: { categories: { elemMatch: { _id: { ne: null } } } }
    ) {
      nodes {
        id
        name {
          ...LocaleStringFields
        }
        slug {
          ...LocaleStringFields
        }
        introduction {
          ...LocaleStringFields
        }
        categories {
          ...JournalCategoryFields
        }
        heroImage {
          asset {
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
        metaImage {
          asset {
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
      }
    }
    page: sanityGeneralPage(id: { eq: $pageId }) {
      id
      name {
        ...LocaleStringFields
      }
      metaname {
        ...LocaleStringFields
      }
      metaDescription {
        ...LocaleStringFields
      }
      metaImage {
        asset {
          fluid(maxWidth: $imageMaxWidth) {
            ...GatsbySanityImageFluid
          }
        }
      }
      className
      metaDescription {
        ...LocaleStringFields
      }
      heroImage {
        asset {
          fluid(maxWidth: $imageMaxWidth) {
            ...GatsbySanityImageFluid
          }
        }
      }
      heroGallery {
        _rawImages
      }
      heroVideoVimeoID
      heroVideoRatioPercent
      _rawSections
      categories {
        ...JournalCategoryFields
      }
    }
  }
`;
